import React from 'react'
import { Legend, Answers, AnswerData } from 'common/types'
import { getQuestionKey } from 'common/utils'


type AppContextType = {
  answers: Answers
  setAnswers: React.Dispatch <React.SetStateAction<Answers>>

  hazardMapIds: string[]
  setHazardMapIds: React.Dispatch <React.SetStateAction<string[]>>

  hazardMapLegends: Legend[]
  setHazardMapLegends: React.Dispatch <React.SetStateAction<Legend[]>>

  loading: boolean
  setLoading: React.Dispatch <React.SetStateAction<boolean>>

  updateAnswer: (q: number, num: number, value: any, valid?: boolean) => void
  updateAnswerByKey: (key: string, value: any, valid?: boolean) => void

  getAnswer: (q: number, num: number) => null | AnswerData
  getAnswerByKey: (key: string) => null | AnswerData

  excludePages: number[]
}

export const AppContext = React.createContext<AppContextType | undefined>(undefined)

export const AppProvider = ({children}: React.PropsWithChildren) => {

  const defaultAnswers = sessionStorage.getItem('answers')

  const [answers, setAnswers] = React.useState<Answers>(defaultAnswers ? JSON.parse(defaultAnswers) : {})
  const [hazardMapIds, setHazardMapIds] = React.useState<string[]>([])

  const legends: Legend[] = []
  const [hazardMapLegends, setHazardMapLegends] = React.useState<Legend[]>(legends)
  const [loading, setLoading] = React.useState<boolean>(false)

  const updateAnswer = (q: number, num: number, value: any, valid?: boolean) => {
    const key = getQuestionKey(q, num)
    setAnswers({
      ...answers,
      [key]: {
        valid: valid === undefined ? true : valid,
        value: value
      }
    })
  }

  const updateAnswerByKey = (key: string, value: any, valid?: boolean) => {
    setAnswers({
      ...answers,
      [key]: {
        valid: valid === undefined ? true : valid,
        value: value
      }
    })
  }

  React.useEffect(() => {
    sessionStorage.setItem('answers', JSON.stringify(answers))
  }, [answers])

  const getAnswer = (q: number, num: number): null | AnswerData => {
    const key = getQuestionKey(q, num)
    return answers[key] || null
  }

  const getAnswerByKey = (key: string): null | AnswerData => {
    return answers[key] || null
  }

  const excludePages = React.useMemo(() => {
    const q1 = getAnswer(1, 1)
    const q2 = getAnswer(2, 1)
    const q4 = getAnswer(4, 1)
    const is_q1_no = q1 === null || q1.value.risk !== 'はい'
    const is_q2_no = q2 === null || q2.value.risk !== 'はい'
    const is_q4_no = q4 === null || q4.value.risk !== 'はい'

    const targets: number[] = []
    if (is_q1_no && is_q4_no) {
      targets.push(5, 6, 7)
    }
    if (is_q2_no) {
      targets.push(8)
    }
    if (is_q4_no) {
      targets.push(9)
    }
    if (is_q2_no && is_q4_no) {
      targets.push(10, 11, 12, 13, 14, 15, 16)
    }
    return targets
  }, [answers])

  const value = React.useMemo(() => ({
    answers, setAnswers,
    hazardMapIds, setHazardMapIds,
    hazardMapLegends, setHazardMapLegends,
    loading, setLoading,
    updateAnswer,
    updateAnswerByKey,
    getAnswer,
    getAnswerByKey,
    excludePages,
  }), [
    answers, setAnswers,
    hazardMapIds, setHazardMapIds,
    hazardMapLegends, setHazardMapLegends,
    loading, setLoading,
    updateAnswer,
    updateAnswerByKey,
    getAnswer,
    getAnswerByKey,
    excludePages,
  ])

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}
